import React, { useMemo, useEffect, useLayoutEffect } from 'react';
import {
  Label,
  TextInput,
  NumberInput,
  Textarea,
  Select,
  TreeSelect,
  Switch,
  DateInput,
  DateTimeInput,
  DateSelect,
  Autocomplete,
  MultiSelect,
  RadioGroup,
  Rating,
  ColorPicker,
} from '../ui';
import type { TreeNode } from '../ui/TreeSelect';
import useSWR from 'swr';
import { useApiOptions } from '../../hooks/useApiOptions';
import { fetcher } from '../../services/fetcher';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import SlateEmailEditor from '../editor/SlateEmailEditor';

export interface FieldConfig {
  name: string;
  inputName?: string;
  label: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  options?: Array<{ value: string | number; label: string; icon?: React.ReactNode } | string>;
  validation?: {
    required?: boolean;
    requiredWhen?: (formData: Record<string, unknown>) => boolean;
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    pattern?: RegExp;
    message?: string;
  };
  // Field-specific props
  rows?: number;
  min?: number;
  max?: number;
  step?: number;
  switchLabel?: string;
  description?: string;
  labelPosition?: 'left' | 'right';
  size?: 'sm' | 'default' | 'lg';
  variant?: 'default' | 'filled' | 'outlined' | 'underlined';
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  helperText?: string;
  autoComplete?: string;
  /** Block browser/password-manager autofill (username, password, etc.). */
  ignoreAutofill?: boolean;
  showCharacterCount?: boolean;
  showClearButton?: boolean;
  /** Shown on the same row as the label (not in minimal layout). */
  labelAfter?: (ctx: {
    getFormData?: () => Record<string, unknown>;
    onChange: (value: unknown) => void;
    isEdit: boolean;
  }) => React.ReactNode;
  // Date field props
  minDate?: string;
  maxDate?: string;
  dateFormat?: string;
  // Autocomplete props
  endpoint?: string;
  // API endpoint for fetching options (for select fields)
  apiEndpoint?: string;
  /** Dynamic endpoint based on form data (e.g. subcategory options when category is selected) */
  optionsEndpointFromFormData?: (formData: Record<string, unknown>) => string | null;
  /** When any of these field names change, dynamic options refetch (e.g. ['defaultCategoryId'] for subcategory) */
  optionsDependOnFields?: string[];
  labelField?: string; // API response field to use as option label
  valueField?: string; // API response field to use as option value (default 'id')
  /** For optionsEndpointFromFormData: narrow rows before mapping (e.g. unpaid invoices, or keep pinned id for edit). */
  filterDynamicOptionItem?: (item: any, formData: Record<string, any>) => boolean;
  /** For optionsEndpointFromFormData: build rich option rows (label + optional description for Select). */
  mapApiItemToSelectOption?: (item: any) => {
    value: string | number;
    label: string;
    description?: string;
    icon?: React.ReactNode;
  };
  /** Sort raw API rows before mapping (e.g. invoice date descending). */
  sortDynamicList?: (items: any[]) => any[];
  /** Sort raw API rows from `apiEndpoint` before mapping; skips default label sort when set. */
  sortApiList?: (items: any[]) => any[];
  // TreeSelect props (hierarchical select)
  treeOptions?: TreeNode[];
  // MultiSelect props
  maxDisplay?: number;
  /** For `multiselect`: show search in dropdown (default true) */
  searchable?: boolean;
  /** For `checkbox-multiselect`: store selected values as numbers (e.g. company ids) */
  checkboxValueNumber?: boolean;
  /** When building options from `apiEndpoint`, drop rows where this returns false */
  filterApiOptionItem?: (item: unknown) => boolean;
  // RadioGroup props
  orientation?: 'vertical' | 'horizontal';
  // Rating props
  precision?: number;
  maxRating?: number;
  showLabel?: boolean;
  // ColorPicker props
  presetColors?: string[];
  // End datetime: calculate this field from start datetime + duration (minutes)
  calculateEndFrom?: {
    startDateTimeField: string;
    durationMinutesField: string;
  };
  // Start datetime: calculate this field from end datetime - duration (minutes)
  calculateStartFrom?: {
    endDateTimeField: string;
    durationMinutesField: string;
  };
  // Slate editor: when this changes, the editor remounts with the new value (e.g. article id when editing)
  contentKey?: string | ((formData: Record<string, any>) => string);
  /** When this field's value changes, run (e.g. fetch payee and prefill category/subcategory). Use setMultipleFieldValues to set several fields at once. getFormData() returns current form values. */
  onChangeSideEffect?: (value: any, setFieldValue: (name: string, value: any) => void, setMultipleFieldValues: (updates: Record<string, any>) => void, getFormData?: () => Record<string, any>) => void | Promise<void>;
  /** When true (select only), run onChangeSideEffect when the dropdown closes (after select/clear), not on change. */
  runSideEffectOnClose?: boolean;
  /** For type 'custom': render a custom component that receives value/onChange (e.g. offer line items editor). */
  customRender?: (props: {
    value: any;
    onChange: (value: any) => void;
    error?: string;
    formData?: Record<string, any>;
    setFieldValue?: (name: string, value: any) => void;
    setMultipleFieldValues?: (updates: Record<string, any>) => void;
  }) => React.ReactNode;
}

export type FormLayoutVariant = 'default' | 'compact' | 'minimal';

interface RenderFieldProps {
  field: FieldConfig;
  value: any;
  onChange: (name: string, value: any) => void;
  setFieldValue?: (name: string, value: any) => void;
  setMultipleFieldValues?: (updates: Record<string, any>) => void;
  onBlur?: (name: string, value: any) => void;
  /** For select: run side effect when dropdown closes (after select/clear). Called with current value. */
  onSelectClose?: (value: any) => void;
  error?: string;
  getFormData?: () => Record<string, any>;
  /** When 'minimal', label is shown inside field (placeholder) and no separate Label is rendered */
  formLayoutVariant?: FormLayoutVariant;
  /** When this changes (e.g. edit data loaded), dynamic-option fields refetch so options use latest form data */
  formKey?: string;
  /** Bumps when a dependency field (e.g. category) changes so dynamic options refetch with new form data */
  formDataVersion?: number;
  /** True when editing an existing record (new entity form is false). */
  isEdit?: boolean;
}

/** Passed from FieldWithDynamicApiOptions so the invoice select remounts when the dependent endpoint changes */
type RenderFieldInternalArgs = RenderFieldProps & {
  processedOptions?: Array<{ value: string | number; label: string; icon?: React.ReactNode } | string>;
  colors?: any;
  /** SWR URL for dynamic selects — included in Select key so options stay in sync when client/etc. changes */
  dynamicOptionsEndpoint?: string | null;
};

// Component wrapper for fields that need API fetching
const FieldWithApiOptions: React.FC<RenderFieldProps> = ({
  field,
  value,
  onChange,
  setFieldValue,
  setMultipleFieldValues,
  onBlur,
  onSelectClose,
  error,
  getFormData,
  formLayoutVariant,
  isEdit,
}) => {
  const { colors } = useTheme();
  // Fetch options from API if apiEndpoint is provided
  const apiEndpoints = useMemo(() => {
    const endpoints: Record<string, { endpoint: string; labelField?: string; valueField?: string }> = {};
    if (field.apiEndpoint) {
      endpoints[field.name] = {
        endpoint: field.apiEndpoint,
        labelField: field.labelField,
        valueField: field.valueField,
      };
    }
    return endpoints;
  }, [field.apiEndpoint, field.labelField, field.valueField, field.name]);

  const apiOptionsData = useApiOptions(apiEndpoints);

  // Process API options: use valueField (default 'id') and labelField; prepend empty option from field.options if present
  const processedOptions = useMemo(() => {
    if (field.apiEndpoint && apiOptionsData[field.name] && apiOptionsData[field.name].length > 0) {
      let apiData = apiOptionsData[field.name];
      if (field.filterApiOptionItem) {
        apiData = apiData.filter((item: unknown) => field.filterApiOptionItem!(item));
      }
      if (field.sortApiList) {
        apiData = field.sortApiList(apiData);
      }
      const valueField = field.valueField ?? 'id';
      const labelField = field.labelField;
      const mapper = field.mapApiItemToSelectOption;
      const fromApi = apiData
        .filter((item: any) => {
          const val = item[valueField];
          return val !== undefined && val !== null;
        })
        .map((item: any) => {
          if (mapper) {
            const m = mapper(item);
            return { value: m.value, label: m.label };
          }
          const value = item[valueField];
          const label =
            labelField && item[labelField] !== undefined && item[labelField] !== null
              ? item[labelField]
              : `Item #${value}`;
          return { value, label: String(label) };
        });
      const sortedFromApi = field.sortApiList
        ? fromApi
        : fromApi.sort((a: { value: any; label: string }, b: { value: any; label: string }) =>
            a.label.localeCompare(b.label),
          );
      // Prepend "None" / empty option if field.options has one (for optional selects)
      const emptyOpt = (field.options || []).find((o: any) => (typeof o === 'object' ? o?.value === '' : o === ''));
      if (emptyOpt) {
        const label = typeof emptyOpt === 'object' ? (emptyOpt as { label?: string }).label : 'None';
        return [{ value: '', label: label || 'None' }, ...sortedFromApi];
      }
      return sortedFromApi;
    }
    return field.options || [];
  }, [
    field.apiEndpoint,
    field.labelField,
    field.valueField,
    field.options,
    apiOptionsData,
    field.name,
    field.mapApiItemToSelectOption,
    field.filterApiOptionItem,
    field.sortApiList,
  ]);

  const result = renderFieldInternal({
    field,
    value,
    onChange,
    setFieldValue,
    setMultipleFieldValues,
    onBlur,
    onSelectClose,
    error,
    processedOptions,
    getFormData,
    colors,
    formLayoutVariant,
    isEdit,
  });
  return result as React.ReactElement | null;
};

// Normalize API response to array
function toOptionsList(raw: unknown): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as { data: any[] }).data)) {
    return (raw as { data: any[] }).data;
  }
  if (raw && typeof raw === 'object' && 'records' in raw && Array.isArray((raw as { records: any[] }).records)) {
    return (raw as { records: any[] }).records;
  }
  return [];
}

// Component for select fields whose options come from an endpoint that depends on form data (e.g. subcategories by category)
const FieldWithDynamicApiOptions: React.FC<RenderFieldProps> = ({
  field,
  value,
  onChange,
  setFieldValue,
  setMultipleFieldValues,
  onBlur,
  onSelectClose,
  error,
  getFormData,
  formLayoutVariant,
  formDataVersion,
  isEdit,
}) => {
  const { colors } = useTheme();
  const formData = getFormData?.() ?? {};
  const endpoint = field.optionsEndpointFromFormData?.(formData) ?? null;
  // Key SWR by the actual API URL so cache always matches the selected client (composite keys could serve stale data).
  const { data: rawData } = useSWR<any>(endpoint, fetcher, {
    revalidateOnFocus: false,
  });
  const list = endpoint ? toOptionsList(rawData) : [];
  const valueField = field.valueField ?? 'id';
  const labelField = field.labelField ?? 'name';
  const processedOptions = useMemo(() => {
    const fd = getFormData?.() ?? {};
    let rows = list;
    if (field.filterDynamicOptionItem) {
      rows = rows.filter((item: any) => field.filterDynamicOptionItem!(item, fd));
    }
    if (field.sortDynamicList) {
      rows = field.sortDynamicList(rows);
    }
    const mapper = field.mapApiItemToSelectOption;
    const fromApi = rows
      .filter((item: any) => item[valueField] !== undefined && item[valueField] !== null)
      .map((item: any) => {
        if (mapper) {
          const m = mapper(item);
          return {
            value: m.value,
            label: m.label,
            description: m.description,
            icon: m.icon,
          };
        }
        return {
          value: item[valueField],
          label: (labelField && item[labelField] != null ? item[labelField] : `Item #${item[valueField]}`) as string,
        };
      });
    const ordered = mapper
      ? fromApi
      : [...fromApi].sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label));
    const emptyOpt = (field.options || []).find((o: any) => (typeof o === 'object' ? o?.value === '' : o === ''));
    if (emptyOpt) {
      const label = typeof emptyOpt === 'object' ? (emptyOpt as { label?: string }).label : 'None';
      return [{ value: '', label: label || 'None' }, ...ordered];
    }
    return ordered;
  }, [
    list,
    endpoint,
    valueField,
    labelField,
    field.options,
    field.filterDynamicOptionItem,
    field.sortDynamicList,
    field.mapApiItemToSelectOption,
    formDataVersion,
    getFormData,
  ]);
  // Clear value when options have loaded and current value is not in the new list (e.g. category changed).
  // Skip for multiselect: value is an array; String(array) never matches option values and would wipe the field every time.
  // Do not clear while options are still loading (only empty option present) so prefilled values (e.g. from payee) are not wiped.
  useEffect(() => {
    const t = field.type;
    if (t === 'multiselect' || t === 'multi-select' || t === 'checkbox-multiselect') {
      return;
    }
    if (endpoint && value !== '' && value !== undefined && value !== null) {
      const optionValues = processedOptions.map((o) => (typeof o === 'object' ? (o as { value: any }).value : o));
      const hasLoadedOptions = optionValues.length > 1 || (optionValues.length === 1 && optionValues[0] !== '' && optionValues[0] !== undefined);
      if (hasLoadedOptions && !optionValues.some((v) => String(v) === String(value))) {
        onChange(field.name, '');
      }
    }
  }, [endpoint, processedOptions, value, field.name, onChange, field.type]);
  return renderFieldInternal({
    field,
    value,
    onChange,
    setFieldValue,
    setMultipleFieldValues,
    onBlur,
    onSelectClose,
    error,
    processedOptions,
    getFormData,
    colors,
    formLayoutVariant,
    formDataVersion,
    dynamicOptionsEndpoint: endpoint,
    isEdit,
  }) as React.ReactElement | null;
};

// Internal render function that doesn't use hooks
const renderFieldInternal = (args: RenderFieldInternalArgs): React.ReactNode => {
  const {
    field,
    value,
    onChange,
    setFieldValue,
    setMultipleFieldValues,
    onBlur,
    onSelectClose,
    error,
    getFormData,
    colors,
    formLayoutVariant,
    formDataVersion,
    dynamicOptionsEndpoint,
    isEdit,
  } = args;
  // When processedOptions was not passed (undefined), use field.options so static select options (e.g. Product/Service) show
  const processedOptions = args.processedOptions;
  const isMinimal = formLayoutVariant === 'minimal';
  const isRequired =
    field.validation?.required ||
    field.required ||
    (typeof field.validation?.requiredWhen === 'function' && getFormData && field.validation.requiredWhen(getFormData()));
  const options = processedOptions !== undefined ? processedOptions : (field.options || []);
  const placeholderOrLabel = isMinimal ? (field.placeholder || field.label) : field.placeholder;

  const handleChange = (newValue: any) => {
    onChange(field.name, newValue);
  };

  const handleBlur = (event?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (onBlur) {
      onBlur(field.name, value);
    }
  };

  const commonProps = {
    id: field.inputName || field.name,
    name: field.inputName || field.name,
    required: isRequired,
    disabled: field.disabled,
    readOnly: field.readOnly,
    error: error,
    helperText: isMinimal ? undefined : field.helperText,
  };

  switch (field.type) {
    case 'hidden':
      return <input key={field.name} type="hidden" name={field.name} value={value || ''} />;

    case 'custom':
      if (field.customRender) {
        return (
          <div key={field.name}>
            {!isMinimal && field.label && (
              <Label htmlFor={field.name} required={isRequired}>
                {field.label}
              </Label>
            )}
            {field.customRender({
              value,
              onChange: handleChange,
              error,
              formData: getFormData?.(),
              setFieldValue,
              setMultipleFieldValues,
            })}
          </div>
        );
      }
      return null;

    case 'textarea':
      return (
        <div key={field.name}>
          {!isMinimal && (
            <Label htmlFor={field.name} required={isRequired}>
              {field.label}
            </Label>
          )}
          <Textarea
            id={commonProps.id}
            name={commonProps.name}
            required={commonProps.required}
            disabled={commonProps.disabled}
            readOnly={commonProps.readOnly}
            error={commonProps.error}
            helperText={commonProps.helperText}
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={placeholderOrLabel}
            rows={field.rows || 4}
            aria-label={isMinimal ? field.label : undefined}
          />
        </div>
      );

    case 'treeSelect': {
      const treeOptions = field.treeOptions ?? [];
      return (
        <div key={field.name}>
          {!isMinimal && (
            <Label htmlFor={field.name} required={isRequired}>
              {field.label}
            </Label>
          )}
          <TreeSelect
            name={field.name}
            value={value !== undefined && value !== null ? value : undefined}
            onChange={(val) => handleChange(val)}
            options={treeOptions}
            placeholder={placeholderOrLabel || `Select ${field.label}`}
            disabled={field.disabled}
            required={isRequired}
            showClearButton={field.showClearButton !== false}
            error={error}
          />
        </div>
      );
    }

    case 'select': {
      const selectValue = value !== undefined && value !== null ? String(value) : '';
      // Use options from field (when provided) so when clientOptions etc. have data we display it
      const rawOptions = options ?? field.options ?? [];
      const customSelectOptions = (Array.isArray(rawOptions) ? rawOptions : [])
        .filter((option) => {
          const optionValue = typeof option === 'object' ? option.value : option;
          return optionValue !== undefined && optionValue !== null;
        })
        .map((option) => {
          const optionValue = typeof option === 'object' ? option.value : option;
          const optionLabel = typeof option === 'object' ? (option.label ?? String(option.value ?? '')) : String(option);
          const optObj = typeof option === 'object' ? (option as { description?: string; icon?: React.ReactNode }) : null;
          return {
            value: optionValue,
            label: optionLabel,
            icon: typeof option === 'object' ? option.icon : undefined,
            description: optObj?.description,
          };
        });
      // Key so Select remounts when dynamic endpoint changes (e.g. client filter) — length alone misses same-count swaps
      const optionsKey =
        field.optionsEndpointFromFormData && dynamicOptionsEndpoint !== undefined
          ? `${field.name}-${dynamicOptionsEndpoint ?? 'none'}-v${formDataVersion ?? 0}-${customSelectOptions.length}`
          : `${field.name}-${customSelectOptions.length}`;

      return (
        <div key={field.name} className="w-full min-w-0">
          {!isMinimal && (
            <Label htmlFor={field.name} required={isRequired}>
              {field.label}
            </Label>
          )}
          <Select
            key={optionsKey}
            name={field.name}
            value={selectValue}
            onChange={(e) => handleChange(e.target.value)}
            onClose={onSelectClose}
            options={customSelectOptions}
            placeholder={placeholderOrLabel || `Select ${field.label}`}
            disabled={field.disabled}
            readOnly={field.readOnly}
            showClearButton={field.showClearButton}
            error={error}
            helperText={commonProps.helperText}
            aria-label={isMinimal ? field.label : undefined}
          />
        </div>
      );
    }

    case 'multiselect':
    case 'multi-select': {
      const multiOptions = (options || []).map((opt) =>
        typeof opt === 'object' ? { value: opt.value, label: opt.label } : { value: opt, label: String(opt) },
      );
      const multiValue: Array<string | number> = Array.isArray(value)
        ? value
        : value === '' || value === undefined || value === null
          ? []
          : [value as string | number];
      return (
        <div key={field.name} className="w-full min-w-0">
          {!isMinimal && (
            <Label htmlFor={field.name} required={isRequired}>
              {field.label}
            </Label>
          )}
          <MultiSelect
            id={field.inputName || field.name}
            options={multiOptions}
            value={multiValue}
            onChange={(newValue) => handleChange(newValue)}
            placeholder={field.placeholder || `Select ${field.label}`}
            maxDisplay={field.maxDisplay ?? 2}
            disabled={field.disabled}
            searchable={field.searchable !== false}
            className={field.disabled ? 'opacity-50 cursor-not-allowed' : ''}
          />
          {field.helperText ? (
            <p className={cn('mt-1 text-xs', colors.textMuted)}>{field.helperText}</p>
          ) : null}
        </div>
      );
    }

    case 'checkbox-multiselect': {
      const multiOptions = (options || []).map((opt) =>
        typeof opt === 'object' ? { value: opt.value, label: opt.label } : { value: opt, label: String(opt) },
      );
      const useNum = field.checkboxValueNumber === true;
      const rawSelected: unknown[] = Array.isArray(value) ? [...value] : [];
      const isChecked = (optVal: string | number): boolean =>
        rawSelected.some((s) =>
          useNum ? Number(s) === Number(optVal) : String(s) === String(optVal),
        );
      const toggle = (optVal: string | number) => {
        if (field.disabled || field.readOnly) return;
        const next = isChecked(optVal)
          ? rawSelected.filter((s) =>
              useNum ? Number(s) !== Number(optVal) : String(s) !== String(optVal),
            )
          : [...rawSelected, useNum ? Number(optVal) : String(optVal)];
        handleChange(next);
      };
      return (
        <div key={field.name} className="w-full min-w-0">
          {!isMinimal && (
            <Label htmlFor={field.name} required={isRequired}>
              {field.label}
            </Label>
          )}
          <div
            className={cn(
              'mt-1 grid grid-cols-1 gap-2 sm:grid-cols-2 max-h-56 overflow-y-auto rounded-md border p-3',
              colors.border,
              colors.surface,
              field.disabled && 'opacity-50 pointer-events-none',
            )}
            role="group"
            aria-label={field.label}
          >
            {multiOptions.length === 0 ? (
              <span className={cn('text-sm', colors.textMuted)}>No options available</span>
            ) : (
              multiOptions.map((opt) => {
                const id = `${field.name}-${String(opt.value)}`;
                return (
                  <label
                    key={id}
                    htmlFor={id}
                    className={cn(
                      'flex items-start gap-2 cursor-pointer rounded px-1 py-0.5 hover:bg-black/5 dark:hover:bg-white/5',
                    )}
                  >
                    <input
                      id={id}
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      checked={isChecked(opt.value)}
                      onChange={() => toggle(opt.value)}
                      disabled={field.disabled}
                    />
                    <span className={cn('text-sm leading-tight', colors.text)}>{opt.label}</span>
                  </label>
                );
              })
            )}
          </div>
          {field.helperText ? (
            <p className={cn('mt-1 text-xs', colors.textMuted)}>{field.helperText}</p>
          ) : null}
        </div>
      );
    }

    case 'radio':
    case 'radiogroup':
      return (
        <div key={field.name}>
          {!isMinimal && (
            <Label htmlFor={field.name} required={isRequired}>
              {field.label}
            </Label>
          )}
          <div className={isMinimal ? '' : 'mt-2'}>
            <RadioGroup
              options={field.options || []}
              value={value}
              onChange={(newValue) => handleChange(newValue)}
              name={field.name}
              required={isRequired}
              disabled={field.disabled}
              orientation={field.orientation || 'vertical'}
            />
          </div>
        </div>
      );

    case 'date':
      return (
        <div key={field.name}>
          {!isMinimal && (
            <Label htmlFor={field.name} required={isRequired}>
              {field.label}
            </Label>
          )}
          <DateInput
            {...commonProps}
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={placeholderOrLabel || 'Select date'}
            aria-label={isMinimal ? field.label : undefined}
            minDate={field.minDate}
            maxDate={field.maxDate}
            dateFormat={field.dateFormat}
            showClearButton={field.showClearButton}
          />
        </div>
      );

    case 'datetime':
    case 'datetime-local': {
      const calcEnd = field.calculateEndFrom;
      const calcStart = field.calculateStartFrom;
      const resolveDurationMinutes = (raw: any): number => {
        if (raw === undefined || raw === null || raw === '') return NaN;

        // First, try to resolve via global duration map (select value = duration id)
        if (typeof window !== 'undefined' && (window as any).__pcrmDurationMinutesMap) {
          const map = (window as any).__pcrmDurationMinutesMap as Record<string, number>;
          const fromMap = map[String(raw)];
          if (typeof fromMap === 'number' && !isNaN(fromMap)) {
            return fromMap;
          }
        }

        // Fallback: if the raw value itself is a number of minutes, use it directly
        const minutes = Number(raw);
        if (!isNaN(minutes)) return minutes;

        return NaN;
      };

      const onCalculateEnd =
        calcEnd && getFormData
          ? () => {
              const formData = getFormData();
              const startRaw = formData[calcEnd.startDateTimeField];
              const durationRaw = formData[calcEnd.durationMinutesField];
              const start = startRaw ? new Date(startRaw) : null;
              const durationMin = resolveDurationMinutes(durationRaw);
              if (start && !isNaN(start.getTime()) && !isNaN(durationMin) && durationMin >= 0) {
                const end = new Date(start.getTime() + durationMin * 60 * 1000);
                handleChange(end.toISOString());
              }
            }
          : undefined;
      const onCalculateStart =
        calcStart && getFormData
          ? () => {
              const formData = getFormData();
              const endRaw = formData[calcStart.endDateTimeField];
              const durationRaw = formData[calcStart.durationMinutesField];
              const end = endRaw ? new Date(endRaw) : null;
              const durationMin = resolveDurationMinutes(durationRaw);
              if (end && !isNaN(end.getTime()) && !isNaN(durationMin) && durationMin >= 0) {
                const start = new Date(end.getTime() - durationMin * 60 * 1000);
                handleChange(start.toISOString());
              }
            }
          : undefined;
      return (
        <div key={field.name}>
          {!isMinimal && (
            <Label htmlFor={field.name} required={isRequired}>
              {field.label}
            </Label>
          )}
          <DateTimeInput
            {...commonProps}
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={placeholderOrLabel || 'Select date and time'}
            aria-label={isMinimal ? field.label : undefined}
            minDate={field.minDate}
            maxDate={field.maxDate}
            showClearButton={field.showClearButton}
          />
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
            {onCalculateEnd && (
              <button
                type="button"
                onClick={onCalculateEnd}
                className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:underline"
              >
                Set from Start + Duration
              </button>
            )}
            {onCalculateStart && (
              <button
                type="button"
                onClick={onCalculateStart}
                className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:underline"
              >
                Set from End − Duration
              </button>
            )}
          </div>
        </div>
      );
    }

    case 'date-select':
    case 'daterange':
      return (
        <div key={field.name}>
          {!isMinimal && (
            <Label htmlFor={field.name} required={isRequired}>
              {field.label}
            </Label>
          )}
          <DateSelect
            value={value || 'all'}
            onChange={(newValue) => handleChange(newValue)}
            placeholder={field.placeholder || 'All Dates'}
          />
        </div>
      );

    case 'autocomplete': {
      // Use resolved options (processedOptions from API or field.options) so edit + API-driven autocomplete show correctly
      const autocompleteOptions: Array<string | { value: string; label: string }> = (options || []).map((option) => {
        if (typeof option === 'string') return option;
        const o = option as { value: string | number; label: string };
        return { value: String(o.value ?? ''), label: o.label ?? String(o.value ?? '') };
      });
      return (
        <div key={field.name}>
          {!isMinimal && (
            <Label htmlFor={field.name} required={isRequired}>
              {field.label}
            </Label>
          )}
          <Autocomplete
            endpoint={field.endpoint}
            options={autocompleteOptions}
            value={value !== undefined && value !== null ? String(value) : ''}
            onChange={(newValue) => handleChange(newValue)}
            placeholder={placeholderOrLabel || 'Type to search...'}
            disabled={field.disabled}
            required={isRequired}
            error={error}
            maxLength={field.validation?.maxLength}
            aria-label={isMinimal ? field.label : undefined}
          />
        </div>
      );
    }

    case 'rating':
      return (
        <div key={field.name}>
          <Rating
            value={value || 0}
            onChange={(newValue) => handleChange(newValue || 0)}
            precision={field.precision || 0.5}
            max={field.maxRating || 5}
            readOnly={field.readOnly}
            disabled={field.disabled}
            showLabel={field.showLabel !== false}
            size={field.size || 'default'}
            name={field.name}
            label={field.label}
            required={isRequired}
            error={error}
            helperText={field.helperText}
          />
        </div>
      );

    case 'switch':
    case 'boolean':
    case 'checkbox':
      return (
        <div key={field.name}>
          {!isMinimal && (
            <Label htmlFor={field.name} required={isRequired}>
              {field.label}
            </Label>
          )}
          <div className={isMinimal ? '' : 'mt-2'}>
            <Switch
              checked={Boolean(value)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target.checked)}
              label={isMinimal ? (field.switchLabel || field.label) : undefined}
              description={field.description}
              labelPosition={field.labelPosition}
              name={field.name}
              size={field.size || 'default'}
              disabled={field.disabled}
            />
          </div>
        </div>
      );

    case 'number':
      // Special handling for taskSpentMin with calculate button
      const showCalculateButton = field.name === 'taskSpentMin' && getFormData;
      const calculateButton = showCalculateButton && colors ? (
        <button
          type="button"
          onClick={() => {
            if (getFormData) {
              const formData = getFormData();
              const startDate = formData.taskStartDate;
              const endDate = formData.taskEndDate;
              
              if (startDate && endDate) {
                try {
                  const start = new Date(startDate);
                  const end = new Date(endDate);
                  
                  if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
                    const diffMs = end.getTime() - start.getTime();
                    const diffMinutes = Math.round(diffMs / (1000 * 60));
                    
                    if (diffMinutes > 0) {
                      handleChange(diffMinutes);
                    }
                  }
                } catch (e) {
                  console.error('Error calculating time:', e);
                }
              }
            }
          }}
          className={`flex items-center justify-center w-10 h-10 ${colors.primary} ${colors.primaryText} rounded-md transition-colors border ${colors.border}`}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.9';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
          title="Calculate from Start/End dates"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
          </svg>
        </button>
      ) : null;
      
      const labelAfterEl =
        field.labelAfter?.({
          getFormData,
          onChange: (v) => handleChange(v),
          isEdit: isEdit ?? false,
        }) ?? null;

      return (
        <div key={field.name}>
          {!isMinimal && (
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <Label htmlFor={field.name} required={isRequired} className="mb-0">
                {field.label}
              </Label>
              {labelAfterEl}
            </div>
          )}
          {isMinimal && labelAfterEl && (
            <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
              <span className={cn('text-xs font-medium', colors?.text ?? 'text-gray-900')}>{field.label}</span>
              {labelAfterEl}
            </div>
          )}
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <NumberInput
                {...commonProps}
                value={value}
                onChange={(e) => handleChange(e.target.value)}
                onBlur={handleBlur}
                placeholder={placeholderOrLabel}
                aria-label={isMinimal ? field.label : undefined}
                min={field.min}
                max={field.max}
                step={field.step}
                prefix={field.prefix}
                suffix={field.suffix}
              />
            </div>
            {calculateButton}
          </div>
        </div>
      );

    case 'color':
      return (
        <div key={field.name}>
          <ColorPicker
            value={value || '#000000'}
            onChange={(newValue) => handleChange(newValue)}
            name={field.name}
            label={field.label}
            required={isRequired}
            disabled={field.disabled}
            readOnly={field.readOnly}
            error={error}
            helperText={field.helperText}
            presetColors={field.presetColors}
          />
        </div>
      );

    case 'richtext':
    case 'rich-text':
      return (
        <div key={field.name}>
          {!isMinimal && (
            <Label htmlFor={field.name} required={isRequired}>
              {field.label}
            </Label>
          )}
          <div className={cn(isMinimal ? '' : 'mt-2', `border ${colors?.border || 'border-gray-300'} rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500`)}>
            <ReactQuill
              theme="snow"
              value={value || ''}
              onChange={(content: string) => handleChange(content)}
              placeholder={placeholderOrLabel || 'Enter content...'}
              readOnly={field.readOnly || field.disabled}
              aria-label={isMinimal ? field.label : undefined}
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                  [{ 'color': [] }, { 'background': [] }],
                  [{ 'align': [] }],
                  ['link', 'image'],
                  ['clean']
                ],
              }}
              formats={[
                'header',
                'bold', 'italic', 'underline', 'strike',
                'list', 'bullet',
                'color', 'background',
                'align',
                'link', 'image'
              ]}
            />
          </div>
          <style>{`
            .ql-container {
              min-height: 300px;
              font-size: 14px;
            }
            .ql-editor {
              min-height: 300px;
            }
            .ql-editor.ql-blank::before {
              color: #9ca3af;
              font-style: normal;
            }
            .ql-toolbar {
              border-top-left-radius: 0.5rem;
              border-top-right-radius: 0.5rem;
              border-bottom: 1px solid #e5e7eb;
            }
            .ql-container {
              border-bottom-left-radius: 0.5rem;
              border-bottom-right-radius: 0.5rem;
            }
          `}</style>
        </div>
      );

    case 'slate': {
      const contentKey =
        typeof field.contentKey === 'function' && getFormData
          ? field.contentKey(getFormData())
          : (field.contentKey as string | undefined);
      return (
        <div key={field.name}>
          {!isMinimal && (
            <Label htmlFor={field.name} required={isRequired}>
              {field.label}
            </Label>
          )}
          <div className={cn(isMinimal ? '' : 'mt-2', 'rounded-lg overflow-hidden border focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500', colors?.border || 'border-gray-300')}>
            <SlateEmailEditor
              key={contentKey ?? 'slate-default'}
              contentKey={contentKey}
              value={value ?? ''}
              onChange={(html: string) => handleChange(html)}
              placeholder={placeholderOrLabel || 'Enter content...'}
              minHeight={320}
              colors={colors}
            />
          </div>
        </div>
      );
    }

    case 'tags':
      // Tags input - comma-separated or array
      const tagsValue = Array.isArray(value) ? value : (value ? String(value).split(',').map(t => t.trim()).filter(t => t) : []);
      const tagsString = tagsValue.join(', ');
      
      return (
        <div key={field.name}>
          {!isMinimal && (
            <Label htmlFor={field.name} required={isRequired}>
              {field.label}
            </Label>
          )}
          <TextInput
            {...commonProps}
            value={tagsString}
            onChange={(e) => {
              const newTags = e.target.value.split(',').map(t => t.trim()).filter(t => t);
              handleChange(newTags);
            }}
            placeholder={placeholderOrLabel || 'Enter tags separated by commas'}
            aria-label={isMinimal ? field.label : undefined}
            onBlur={(e) => {
              const newTags = e.target.value.split(',').map(t => t.trim()).filter(t => t);
              handleChange(newTags);
              if (onBlur) onBlur(field.name, newTags);
            }}
          />
          {tagsValue.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {tagsValue.map((tag, idx) => (
                <span
                  key={idx}
                  className={`inline-flex items-center gap-1 px-2 py-1 ${colors?.primary || 'bg-indigo-100'} ${colors?.primaryText || 'text-indigo-800'} text-xs font-medium rounded`}
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => {
                      const newTags = tagsValue.filter((_, i) => i !== idx);
                      handleChange(newTags);
                    }}
                    className="hover:opacity-70"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      );

    case 'text':
    case 'email':
    case 'tel':
    case 'url':
    default:
      return (
        <div key={field.name}>
          {!isMinimal && (
            <Label htmlFor={field.name} required={isRequired}>
              {field.label}
            </Label>
          )}
          <TextInput
            {...commonProps}
            type={field.type || 'text'}
            value={value !== undefined && value !== null ? String(value) : ''}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={handleBlur}
            placeholder={placeholderOrLabel}
            autoComplete={field.autoComplete}
            ignoreAutofill={field.ignoreAutofill ?? field.type === 'password'}
            aria-label={isMinimal ? field.label : undefined}
            maxLength={field.validation?.maxLength}
            minLength={field.validation?.minLength}
            showCharacterCount={field.showCharacterCount}
            showClearButton={field.showClearButton}
            size={field.size}
            variant={field.variant}
            prefix={field.prefix}
            suffix={field.suffix}
          />
        </div>
      );
  }
};

// Wrapper component that uses the hook
const RenderFieldWrapper: React.FC<RenderFieldProps> = (props) => {
  const { colors } = useTheme();
  const { field, value, onChange, setFieldValue, setMultipleFieldValues, onBlur, onSelectClose, error, getFormData, formLayoutVariant, formKey, formDataVersion, isEdit } = props;
  
  if (field.optionsEndpointFromFormData && getFormData) {
    return <FieldWithDynamicApiOptions key={field.name} field={field} value={value} onChange={onChange} setFieldValue={setFieldValue} setMultipleFieldValues={setMultipleFieldValues} onBlur={onBlur} onSelectClose={onSelectClose} error={error} getFormData={getFormData} formLayoutVariant={formLayoutVariant} formKey={formKey} formDataVersion={formDataVersion} isEdit={isEdit} />;
  }
  if (field.apiEndpoint) {
    return <FieldWithApiOptions key={field.name} field={field} value={value} onChange={onChange} setFieldValue={setFieldValue} setMultipleFieldValues={setMultipleFieldValues} onBlur={onBlur} onSelectClose={onSelectClose} error={error} getFormData={getFormData} formLayoutVariant={formLayoutVariant} isEdit={isEdit} />;
  }
  return renderFieldInternal({ field, value, onChange, setFieldValue, setMultipleFieldValues, onBlur, onSelectClose, error, getFormData, colors, formLayoutVariant, isEdit }) as React.ReactElement | null;
};

// Export the main renderField function that handles API fetching
// Returns a React element (component) so hooks can be used
export const renderField = (props: RenderFieldProps): React.ReactElement => {
  return <RenderFieldWrapper key={props.field.name} {...props} />;
};

